import React, {useState, useEffect} from "react";
import {useFormik} from "formik";
import {connect} from "react-redux";
import {Form, InputGroup, Button} from 'react-bootstrap';
import * as Yup from "yup";
import {actions} from "../../../../redux/modules/auth/actions";
import {changePassword, sendMobileCert, validMobileCert} from "../../../../redux/modules/auth/crud";
import {onlyNum} from "../../../../utils/formHelpers";
import styles from "./styles.module.scss";

const initialValues = {
  adminId: "",
  telNo: "",
  authNo: "",
  password: "",
  passwordConfirm: "",
};

let interval;

function ConvertTimer(num) {
  const value = `0${parseInt(num / 60)}`.slice(-2);
  const remainder = `0${num % 60}`.slice(-2);
  return `${value}:${remainder}`;
}

function ForgotPassword({ history }) {
  const [isRequested, setIsRequested] = useState(false);
  const [isValid, setValid] = useState(false);
  const [remain, setRemain] = useState(0);

  const ForgotPasswordSchema = Yup.object().shape({
    adminId: Yup
      .string()
      .required("사용자 아이디를 입력하세요"),
    telNo: Yup
      .number()
      .typeError("숫자만 입력 가능합니다.")
      .required("전화번호를 입렭하세요."),
    authNo: Yup
      .number()
      .typeError("숫자만 입력 가능합니다."),
    password: Yup
      .string()
      .required("비밀번호를 입력하세요.")
      .min(8, "8자 이상 입력하세요.")
      .max(50, "50자 이하로 입력하세요."),
    passwordConfirm: Yup
      .string()
      .required("비밀번호를 한번 더 입력하세요.")
      .when("password", {
        is: val => val && val.length > 0 ? true : false,
        then: Yup
          .string()
          .oneOf([Yup.ref("password")], "입력한 비밀번호와 일치하지 않습니다."),
      }),
  });

  const getInputClasses = (fieldname, subCondition = true) => {
    if (form.touched[fieldname] && form.errors[fieldname]) {
      return "is-invalid";
    }
    if (form.touched[fieldname] && !form.errors[fieldname] && subCondition) {
      return "is-valid";
    }
    return "";
  };

  const handleSendCert = () => {
    const {telNo} = form.values;
    sendMobileCert(telNo).then(() => {
      setIsRequested(true);
      setRemain(180);
    }).catch(error => {
      alert(error.message);
    });
  };

  const handleValidCert = () => {
    const {authNo, telNo} = form.values;
    validMobileCert({authNo, telNo}).then(() => {
      setValid(true);
      clearTimeout(interval);
      form.setFieldTouched('authNo', true);
    }).catch(err => {
      alert(err.message);
    });
  };

  const form = useFormik({
    initialValues,
    validationSchema: ForgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      const {adminId, password, telNo} = form.values;

      changePassword({adminId, password, telNo})
        .then(() => {
          alert('비밀번호가 변경 되었습니다.');
          history.goBack();
        })
        .catch(err => {
          alert(err.message);
          setSubmitting(false);
        });
    },
  });

  useEffect(() => {
    if (!remain) return clearTimeout(interval);
    interval = setTimeout(() => setRemain(remain - 1), 1000);
    return () => clearTimeout(interval);
  }, [remain]);

  return (
    <div className="login-form login-forgot" style={{display: "block"}}>
      {/* begin::Head */}
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">비밀번호 찾기</h3>
        <div className="text-muted font-weight-bold">
          ID와 전화번호 인증 후 비밀번호 재설정이 가능합니다.
        </div>
      </div>
      {/* end::Head */}

      <Form
        id="forgot-password-form"
        className="form fv-plugins-bootstrap fv-plugins-framework animated animate__animated animate__backInUp"
        onSubmit={form.handleSubmit}
      >

        {form.status && (
          <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
            <div className="alert-text font-weight-bold">
              {form.status}
            </div>
          </div>
        )}

        {/* begin: Account */}
        <Form.Group controlId="adminId">
          <Form.Label>
            <span className="text-danger mr-2 align-middle">*</span>
            <span className="font-weight-boldest">ID</span>
          </Form.Label>
          <Form.Control
            placeholder="아이디"
            type="text"
            name="adminId"
            className={`form-control h-auto py-5 px-6 ${getInputClasses("adminId")}`}
            {...form.getFieldProps("adminId")}
          />
          {form.touched.adminId && form.errors.adminId ? (
            <div className="fv-plugins-message-container">
              <Form.Text className="fv-help-block">{form.errors.adminId}</Form.Text>
            </div>
          ) : (
            <Form.Text className="text-muted">
              ID를 입력해주세요.
            </Form.Text>
          )}
        </Form.Group>
        {/* end: Account */}

        {!isValid && (
          <>
            {/* begin: Mobile */}
            <Form.Group controlId="mobile">
              <Form.Label>
                <span className="text-danger mr-2 align-middle">*</span>
                <span className="font-weight-boldest">전화번호</span>
              </Form.Label>
              <InputGroup className="mb-2 mr-sm-2">
                <Form.Control
                  placeholder="- 없이 입력"
                  type="text"
                  name="telNo"
                  onKeyDown={onlyNum}
                  className={`form-control h-auto py-5 px-6 ${getInputClasses("telNo", isValid)}`}
                  {...form.getFieldProps("telNo")}
                />
                <InputGroup.Append>
                  <Button
                    type="button"
                    variant="primary"
                    className="w-120px font-weight-boldest font-size-h6"
                    disabled={isValid || form.errors.telNo || !form.values.telNo}
                    onClick={handleSendCert}
                  >
                    {isRequested ? '재 전송' : '인증번호 전송'}
                  </Button>
                </InputGroup.Append>
              </InputGroup>
              {form.touched.telNo && form.errors.telNo ? (
                <div className="fv-plugins-message-container">
                  <Form.Text className="fv-help-block">{form.errors.telNo}</Form.Text>
                </div>
              ) : (
                <Form.Text className="text-muted">
                  인증번호 전송은 통신사에 따라 최대 1분까지 소요될 수 있습니다.<br/>
                  인증번호가 도착하지 않을 경우 ‘재전송’을 눌러주세요.
                </Form.Text>
              )}
            </Form.Group>
            {/* end: Mobile */}

            {/* begin: Mobile Cert */}
            {isRequested && (
              <Form.Group controlId="mobileCert">
                <InputGroup className="mb-2 mr-sm-2">
                  <Form.Control
                    placeholder={remain === 0 ? "인증시간 만료" : "인증번호 입력"}
                    type="text"
                    name="authNo"
                    onKeyDown={onlyNum}
                    disabled={remain === 0}
                    className={`form-control h-auto py-5 px-6 ${getInputClasses("authNo", isValid)}`}
                    {...form.getFieldProps("authNo")}
                  />
                  {!isValid && (
                    <span className={styles.timer}>
                  {ConvertTimer(remain)}
                </span>
                  )}
                  <InputGroup.Append>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={remain === 0 || isValid || form.values.authNo.length !== 6}
                      className="w-120px font-weight-boldest font-size-h6"
                      onClick={handleValidCert}
                    >
                      인증
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
                {form.touched.authNo && form.errors.authNo ? (
                  <div className="fv-plugins-message-container">
                    <Form.Text className="fv-help-block">{form.errors.authNo}</Form.Text>
                  </div>
                ) : null}
              </Form.Group>
            )}
            {/* end: Mobile Cert */}
          </>
        )}

        {isValid && (
          <>
            {/* begin: Password */}
            <Form.Group controlId="password">
              <Form.Label>
                <span className="text-danger mr-2 align-middle">*</span>
                <span className="font-weight-boldest">비밀번호 재설정</span>
              </Form.Label>
              <Form.Control
                placeholder="비밀번호"
                type="password"
                name="password"
                className={`form-control h-auto py-5 px-6 ${getInputClasses("password")}`}
                {...form.getFieldProps("password")}
              />
              {form.touched.password && form.errors.password ? (
                <div className="fv-plugins-message-container">
                  <Form.Text className="fv-help-block">{form.errors.password}</Form.Text>
                </div>
              ) : (
                <Form.Text className="text-muted">
                  4~6자리 영문자와 숫자 조합으로 입력해주세요.
                </Form.Text>
              )}
            </Form.Group>
            {/* end: Password */}

            {/* begin: Confirm Password */}
            <Form.Group controlId="passwordConfirm">
              <Form.Label>
                <span className="text-danger mr-2 align-middle">*</span>
                <span className="font-weight-boldest">비밀번호 확인</span>
              </Form.Label>
              <Form.Control
                placeholder="비밀번호 확인"
                type="password"
                name="passwordConfirm"
                className={`form-control h-auto py-5 px-6 ${getInputClasses("passwordConfirm")}`}
                {...form.getFieldProps("passwordConfirm")}
              />
              {form.touched.passwordConfirm && form.errors.passwordConfirm ? (
                <div className="fv-plugins-message-container">
                  <Form.Text className="fv-help-block">{form.errors.passwordConfirm}</Form.Text>
                </div>
              ) : (
                <Form.Text className="text-muted">
                  확인을 위해 비밀번호를 한번 더 입력해주세요.
                </Form.Text>
              )}
            </Form.Group>
            {/* end: Confirm Password */}
          </>
        )}

        <div className="form-group d-flex flex-wrap flex-center">
          <div className="w-50 px-4 my-3">
            <button
              type="button"
              className="btn btn-secondary font-weight-bold px-9 py-4 w-100"
              onClick={() => history.goBack()}
            >
              취소
            </button>
          </div>
          <div className="w-50 px-4 my-3">
            <button
              type="submit"
              disabled={form.isSubmitting || !form.isValid || !isValid}
              className="btn btn-primary font-weight-bold px-9 py-4 w-100"
            >
              <span>비밀번호 재설정</span>
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default connect(null, actions)(ForgotPassword);
